import { PowerPlantType, PowerPlantSpec } from "../types/powerPlants";

// Base power plant specifications from Traveller rulebook
export const POWER_PLANTS: Record<PowerPlantType, PowerPlantSpec> = {
  fission: {
    type: "fission",
    name: "Fission Power Plant",
    baseTons: 1,
    baseCost: 0.4,
    basePower: 8,
    powerPerTon: 8,
    costPerTon: 0.4, // MCr
    techLevel: 6,
    description: "Basic fission-based power generation system",
    maintenanceCost: 0.1, // MCr per month
    minRating: 1,
    maxRating: 6,
  },
  chemical: {
    type: "chemical",
    name: "Chemical Power Plant",
    baseTons: 1,
    baseCost: 0.25,
    basePower: 5,
    powerPerTon: 5,
    costPerTon: 0.25,
    techLevel: 7,
    description: "Chemical power generation system",
    maintenanceCost: 0.08,
    minRating: 1,
    maxRating: 6,
  },
  fusion: {
    type: "fusion",
    name: "Fusion Power Plant",
    baseTons: 1,
    baseCost: 0.5,
    basePower: 10,
    powerPerTon: 10,
    costPerTon: 0.5,
    techLevel: 8,
    description: "Fusion power generation system",
    maintenanceCost: 0.15,
    minRating: 1,
    maxRating: 6,
  },
  fusion12: {
    type: "fusion12",
    name: "Fusion Power Plant (TL12)",
    baseTons: 1,
    baseCost: 1,
    basePower: 15,
    powerPerTon: 15,
    costPerTon: 1,
    techLevel: 12,
    description: "Advanced fusion power generation system",
    maintenanceCost: 0.2,
    minRating: 1,
    maxRating: 6,
  },
  fusion15: {
    type: "fusion15",
    name: "Fusion Power Plant (TL15)",
    baseTons: 1,
    baseCost: 2,
    basePower: 20,
    powerPerTon: 20,
    costPerTon: 2,
    techLevel: 15,
    description: "High-tech fusion power generation system",
    maintenanceCost: 0.25,
    minRating: 1,
    maxRating: 6,
  },
  antimatter: {
    type: "antimatter",
    name: "Antimatter Power Plant",
    baseTons: 1,
    baseCost: 10,
    basePower: 100,
    powerPerTon: 100,
    costPerTon: 10,
    techLevel: 20,
    description: "Cutting-edge antimatter power generation system",
    maintenanceCost: 0.5,
    minRating: 1,
    maxRating: 6,
  },
} as const;

// Power requirements from Traveller rulebook
export const POWER_REQUIREMENTS = {
  BASIC_SYSTEMS: {
    PERCENT: 0.2, // 20% of hull tonnage
    DESCRIPTION: "Basic ship systems power requirement",
  },
  MANEUVER_DRIVE: {
    PERCENT: 0.1, // 10% of hull tonnage per thrust
    DESCRIPTION: "Power required per thrust point",
    THRUST_0_MULTIPLIER: 0.25, // 25% of normal power for thrust 0
  },
  JUMP_DRIVE: {
    PERCENT: 0.1, // 10% of hull tonnage per jump rating
    DESCRIPTION: "Power required per jump rating",
  },
  WEAPONS: {
    PERCENT: 0.05, // 5% of hull tonnage per weapon
    DESCRIPTION: "Power required per weapon system",
  },
  SENSORS: {
    PERCENT: 0.02, // 2% of hull tonnage
    DESCRIPTION: "Power required for sensor systems",
  },
} as const;

// Helper functions
export const getPowerPlantSpec = (type: PowerPlantType): PowerPlantSpec => {
  return POWER_PLANTS[type];
};

export const calculatePowerPlantTons = (
  powerRequired: number,
  type: PowerPlantType,
): number => {
  const spec = getPowerPlantSpec(type);
  return Math.ceil(powerRequired / spec.powerPerTon);
};

export const calculatePowerPlantCost = (
  tons: number,
  type: PowerPlantType,
): number => {
  const spec = getPowerPlantSpec(type);
  return tons * spec.costPerTon;
};

export const calculateMonthlyMaintenance = (
  tons: number,
  type: PowerPlantType,
): number => {
  const spec = getPowerPlantSpec(type);
  return tons * spec.maintenanceCost;
};

// Validation functions
export const validatePowerPlantSpec = (spec: PowerPlantSpec): string[] => {
  const errors: string[] = [];

  if (!spec.type) {
    errors.push("Power plant type is required");
  }

  if (!spec.name) {
    errors.push("Power plant name is required");
  }

  if (spec.powerPerTon <= 0) {
    errors.push("Power per ton must be positive");
  }

  if (spec.costPerTon <= 0) {
    errors.push("Cost per ton must be positive");
  }

  if (spec.techLevel < 0) {
    errors.push("Tech level cannot be negative");
  }

  if (spec.maintenanceCost < 0) {
    errors.push("Maintenance cost cannot be negative");
  }

  return errors;
};

export const validatePowerRequirements = (
  powerRequired: number,
  hullTons: number,
): string[] => {
  const errors: string[] = [];

  if (powerRequired < 0) {
    errors.push("Power required cannot be negative");
  }

  if (hullTons <= 0) {
    errors.push("Hull tonnage must be positive");
  }

  const basicSystemsPower = hullTons * POWER_REQUIREMENTS.BASIC_SYSTEMS.PERCENT;
  if (powerRequired < basicSystemsPower) {
    errors.push(
      `Power required (${powerRequired}) is less than basic systems requirement (${basicSystemsPower})`,
    );
  }

  return errors;
};
